export interface NearestBoundary {
  distance_meters: number
  /** Same distance pre-converted to statute miles (1 mile = 1609.344 m). */
  distance_miles: number
  /**
   * Bearing from the query point to the nearest edge point, in degrees
   * (0..360) clockwise from north. Null when the point lies exactly on the
   * boundary — `ST_Azimuth` is undefined for coincident points. Treat null
   * as "no cardinal direction" and render just the distance.
   */
  bearing_degrees: number | null
  /**
   * 16-point cardinal label derived server-side from `bearing_degrees`
   * (`N`, `NNE`, `NE`, …, `NNW`). Null when `bearing_degrees` is null.
   */
  cardinal: string | null
}

/** A GeoJSON Polygon, MultiPolygon, or Point geometry. */
export interface DisplayGeometry {
  type: 'Polygon' | 'MultiPolygon' | 'Point'
  // number[]                — Point (lon, lat)
  // number[][][]            — Polygon (rings)
  // number[][][][]          — MultiPolygon (polygons of rings)
  coordinates: number[] | number[][][] | number[][][][]
}

/**
 * Inline source attribution attached to every hit. Mirrors the same shape
 * the API returns so consumers can render "Source: TIGER/Line 2024
 * (Census)" without a second round trip to /v1/sources.
 */
export interface HitSource {
  id: string
  name: string
  org: string | null
  url: string | null
  vintage: string | null
  effective_date: string | null
  license: string | null
}

/**
 * What lies on the OTHER side of the closest border edge. Only present on
 * country-layer hits (null on every other layer). `kind: 'country'` carries
 * the adjacent country's name (e.g. "Mexico", "Canada"); `kind: 'water'`
 * means the closest edge is coastline / Great Lakes / etc. and there's no
 * adjacent country.
 */
export interface BorderAdjacent {
  kind: 'country' | 'water'
  name: string | null
}

export interface LocationHit {
  layer_id: string
  name: string
  geoborder_id: string
  source_id: string
  vintage: string | null
  effective_date: string | null
  identifiers: Record<string, unknown>
  properties: Record<string, unknown>
  nearest_boundary: NearestBoundary
  /**
   * Simplified geometry suitable for map rendering. The public endpoint
   * always includes it (the homepage map needs it); the developer-API
   * /v1/location path returns this as null and exposes geometry separately
   * via /v1/boundaries/{id}/geometry.
   */
  display_geom: DisplayGeometry | null
  /** Inline source attribution for this specific hit. */
  source: HitSource | null
  /**
   * Only set on country hits. Describes what's just past the closest border
   * edge — adjacent country name, or "water" for coastlines / Great Lakes.
   */
  border_adjacent?: BorderAdjacent | null
}

/**
 * Set when the click lands outside every country polygon — i.e.
 * international waters. The SPA renders this as an "International
 * waters" row + a directional distance to the named country.
 * Null otherwise (any land click).
 */
export interface NearestCountry {
  geoborder_id: string
  name: string
  distance_meters: number
  distance_miles: number
  bearing_degrees: number | null
  cardinal: string | null
}

/**
 * Distance + bearing to the closest point on the U.S. national boundary,
 * computed against the dissolved-from-states TIGER geometry. Returned on
 * every response — for U.S.-inside points it mirrors the country hit's
 * own `nearest_boundary`; for U.S.-outside points it's a useful new datum.
 *
 * `adjacent_country` names the foreign country across the closest U.S.
 * edge for in-U.S. points (e.g. "Mexico"); null for out-of-U.S. points
 * (the answer would just be "United States", which isn't useful).
 *
 * `label` is a composed display string suitable for direct rendering —
 * `"U.S. border with Mexico"` when `adjacent_country` is set,
 * `"U.S. border"` otherwise.
 */
export interface NearestUsaBorder {
  distance_meters: number
  distance_miles: number
  bearing_degrees: number | null
  cardinal: string | null
  adjacent_country: string | null
  label: string
}

/**
 * Signed distance to the survey-grade definitive U.S. border (IBWC + IBC).
 * Positive when inside the United States, negative when outside.
 */
export interface DistanceToUsBorder {
  distance_meters: number
  distance_miles: number
  distance_feet: number
  bearing_degrees: number | null
  cardinal: string | null
}

export interface LocationResult {
  query: { lat: number; lon: number }
  hits: LocationHit[]
  /** Only present when hits is empty (international waters). */
  nearest_country?: NearestCountry | null
  /** Always present. Distance + bearing to the U.S. national boundary. */
  nearest_usa_border?: NearestUsaBorder | null
  /**
   * Authoritative "is this coordinate in the United States?" per the
   * survey-grade definitive border. Accurate to the surveyed line even a
   * few feet from the border.
   */
  in_united_states?: boolean
  /** Signed distance to the definitive U.S. border (+ inside, − outside). */
  distance_to_us_border?: DistanceToUsBorder | null
  generated_at: string
}

/** Error thrown when the public endpoint rate-limits the caller. */
export class RateLimitedError extends Error {
  retryAfterSeconds: number
  constructor(message: string, retryAfterSeconds: number) {
    super(message)
    this.name = 'RateLimitedError'
    this.retryAfterSeconds = retryAfterSeconds
  }
}

/**
 * Geocode a coordinate (reverse-geocode in strict GIS terms — coordinate
 * → place context) via the same-origin `/api/public/location` endpoint.
 * Netlify rewrites the path onto the public-location Edge Function which
 * is Origin-gated and per-IP rate-limited. No Bearer key needed; the SPA
 * always calls from an allowed origin.
 */
export async function geocode(
  lon: number,
  lat: number,
  opts: { strictUsBorder?: boolean } = {},
): Promise<LocationResult> {
  const strict = opts.strictUsBorder ? '&strict_us_border=true' : ''
  const url = `/api/public/location?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}${strict}`
  const res = await fetch(url, { method: 'GET' })
  if (res.status === 429) {
    // application/problem+json carries `detail` + the `retry_after` extension.
    const body = (await res.json().catch(() => ({}))) as { detail?: string; retry_after?: number }
    throw new RateLimitedError(
      body.detail ?? 'Too many requests from this device. Please slow down and try again in a moment.',
      body.retry_after ?? 60,
    )
  }
  if (!res.ok) {
    let message = `Public location lookup failed (status ${res.status})`
    try {
      // The server now returns RFC 7807 problem+json: { type, title, status,
      // detail, code, ... }. Fall back to `error` (legacy) and `title` for
      // belt-and-suspenders.
      const body = (await res.json()) as { detail?: string; title?: string; error?: string }
      if (body.detail) message = body.detail
      else if (body.title) message = body.title
      else if (body.error) message = body.error
    } catch {
      /* leave message as default */
    }
    throw new Error(message)
  }
  return (await res.json()) as LocationResult
}

/** Find the hit for a given layer_id, if any. */
export function findHit(result: LocationResult, layerId: string): LocationHit | undefined {
  return result.hits.find((h) => h.layer_id === layerId)
}

// The cardinal()/metersToMiles() helpers that used to live here were
// retired in favor of server-side equivalents — the API now emits
// `cardinal` and `distance_miles` on every nearest-boundary block, so
// the SPA reads them directly off the response. `src/lib/measure.ts`
// still exports its own `metersToMiles` for non-API code (e.g. the
// measure tool in /explore).
